import {_} from 'lodash';
import {Permission} from './Permission';
import {
    IRouteDefinition,
    ITranslationParams,
    TRouteId,
    TRouteIdOrDefinition,
} from '../index';
import {getEadmRole} from '../util/misc';
import {getDef, getRawRouteDefinitions} from './routeDefinitionsData';

export {isRouteDefinition, findRouteDefinitionByHashPattern, getDef} from './routeDefinitionsData';

export function getRouteDefinitions(): IRouteDefinition[] {
    return getRawRouteDefinitions()
        .map((o) => getRouteDefinition(o))
        .filter((d): d is IRouteDefinition => d !== null);
}

/**
 * Returns a complete route definition, meaning: it contains a complete set of attributes
 * and with a self reference in the component.params.__routeDef attribute.
 */
export function getRouteDefinition(
    idOrDef: TRouteIdOrDefinition,
    override: Partial<IRouteDefinition> = {},
): IRouteDefinition | null {
    if (!idOrDef) {
        return null;
    }
    let def: IRouteDefinition;
    if (typeof idOrDef === 'string') {
        const resolved = getDef(idOrDef as TRouteId);
        if (!resolved) {
            return null;
        }
        def = resolved;
    } else {
        def = idOrDef as IRouteDefinition;
    }
    let ret: IRouteDefinition;
    if ('_extend' in def && def._extend !== undefined) {
        const defs: IRouteDefinition[] = [
            def,
            ...getGraph(def._extend)
                .map((id) => getDef(id))
                .filter((d): d is IRouteDefinition => d !== undefined),
        ];
        ret = _.defaultsDeep({}, defs[0], ...defs.slice(1));
        // restore the original arrays
        (['customButtons', 'tabs', 'subItems'] as const).forEach((k) => {
            if (k in def) {
                (ret as Record<typeof k, IRouteDefinition[typeof k]>)[k] = def[k];
            }
        });
    } else {
        ret = {...def};
    }
    _.defaultsDeep(ret, {
        canSeeOwnPage: false,
        hashPrefixOrgId: true,
        level: 1,
        permissionLevel: Permission.CompanyAdmin,
        showHeading: true,
        showMultiOperations: true,
        showTabs: true,
    });
    // Empty string must not win over `label` — parent stubs get `''` here and inherit via `_extend`.
    ret.labelLong = ret.labelLong || ret.label || '';
    if (ret.subItems === null && ret.child) {
        ret.subItems = [ret.child];
    }
    ret.tabs = ret.tabs?.map((tab: TRouteIdOrDefinition) => {
        const tabDef = setRouteDefaults(tab, {level: 2});
        return getRouteDefinition(tabDef);
    }).filter((d): d is IRouteDefinition => d !== null) ?? null;
    const cpy = _.clone(ret);
    delete cpy.component;
    ret = _.defaultsDeep(override, ret, {component: {params: {}}});
    if (ret.component) {
        ret.component.params.__routeDef = cpy;
    }
    return ret;
}

export function isRoutePermissionOkay(
    idOrDef: TRouteIdOrDefinition,
    permissionLevel: Permission = getEadmRole(),
): boolean {
    const def = getRouteDefinition(idOrDef);
    if (!def) {
        return false;
    }
    return (def.permissionLevel ?? Permission.None) <= permissionLevel;
}

function setRouteDefaults(def: TRouteIdOrDefinition, obj: Partial<IRouteDefinition>): IRouteDefinition {
    let ret: Partial<IRouteDefinition>;
    if (typeof def === 'string') {
        ret = {_extend: def};
    } else {
        ret = def as IRouteDefinition;
    }
    return _.defaultsDeep({}, obj, ret);
}

function getGraph(id: TRouteId | readonly TRouteId[]): TRouteId[] {
    if (Array.isArray(id)) {
        return _(id)
            .map((_id: TRouteId) => _getGraph(_id))
            .flatten()
            .value();
    } else {
        return _getGraph(id as TRouteId);
    }
}

function _getGraph(id: TRouteId, graph: TRouteId[] = []): TRouteId[] {
    graph.push(id);
    const def = getDef(id);
    if (!def) {
        return graph;
    }
    if ('_extend' in def && def._extend !== undefined) {
        const _extend = def._extend;
        if (Array.isArray(_extend)) {
            return getGraph(_extend);
        } else {
            return _getGraph(_extend as TRouteId, graph);
        }
    } else {
        return graph;
    }
}

export function getRouteLabel(
    idOrDef: TRouteIdOrDefinition,
    defaults?: Partial<ITranslationParams>,
): string {
    const def = getRouteDefinition(idOrDef);
    if (!def) {
        return '';
    }
    const strOrFn = def.labelLong || def.label;
    if (typeof strOrFn === 'function') {
        return strOrFn();
    }
    return strOrFn ?? '';
}

export function getTopParent(idOrDef: TRouteIdOrDefinition): IRouteDefinition | null {
    const def = getRouteDefinition(idOrDef);
    if (!def) {
        return null;
    }
    if (def.parent) {
        return getTopParent(def.parent);
    }
    return def;
}

/**
 * Search/list route for an object detail (e.g. manualuserdetails → manualusersearch).
 * Falls back to the top parent hub when no list route is registered.
 */
export function getListRouteForDetail(detailDef: IRouteDefinition): IRouteDefinition {
    const candidates = getRouteDefinitions().filter((r) => r.child === detailDef.id);
    if (candidates.length === 1) {
        return candidates[0] ?? detailDef;
    }
    if (candidates.length > 1) {
        if (detailDef.manual) {
            const manual = candidates.find((r) => r.manual);
            if (manual) {
                return manual;
            }
        }
        const search = candidates.find((r) => r.isSearch || r.serviceName === 'searchobject');
        if (search) {
            return search;
        }
        return candidates[0] ?? detailDef;
    }
    return getTopParent(detailDef) ?? detailDef;
}

export function getParent(idOrDef: TRouteIdOrDefinition): IRouteDefinition | null {
    const def = getRouteDefinition(idOrDef);
    if (!def?.parent) {
        return null;
    }
    return getRouteDefinition(def.parent);
}
